import React, { useState, useCallback, useRef, useEffect } from "react";
import CardBox from "./CardBox";
import BorderDiagram from "./BorderDiagram";
import Loupe from "./Loupe";
import { measure, asRatio, PSA_TOLERANCE } from "./centeringMath";

/**
 * Is this card centred well enough to be worth grading?
 *
 * A photo, two boxes — the card's outer edge and the inner edge of its border —
 * and the four gaps between them are the borders. The ratio falls out of that.
 *
 * The picture never leaves the phone. It is read straight into an object URL
 * and drawn; nothing is uploaded, nothing is stored, and there is no server
 * side to this screen at all. That keeps it free to run, keeps the privacy page
 * honest, and means it still works with no signal — which is the one place a
 * person is most likely to be standing when they want it.
 */

// Roughly where a card's border sits, so the boxes open somewhere useful rather
// than as two dots in a corner.
const START_OUTER = { x: 0.08, y: 0.06, w: 0.84, h: 0.88 };
const START_INNER = { x: 0.14, y: 0.12, w: 0.72, h: 0.76 };

export default function Centering() {
  const [src, setSrc] = useState(null);
  const [side, setSide] = useState("front");
  const [outer, setOuter] = useState(START_OUTER);
  const [inner, setInner] = useState(START_INNER);
  const [error, setError] = useState("");
  // Where a drag currently is, for the magnifier. Null when nothing is moving.
  const [probe, setProbe] = useState(null);
  const fileRef = useRef(null);
  // Object URLs hold the file in memory until they are given back.
  const urlRef = useRef(null);

  useEffect(
    () => () => {
      if (urlRef.current) URL.revokeObjectURL(urlRef.current);
    },
    []
  );

  const take = useCallback((file) => {
    if (!file) return;
    if (!file.type?.startsWith("image/")) {
      setError("That's not an image — a photo or a scan of the card is what this needs.");
      return;
    }
    if (urlRef.current) URL.revokeObjectURL(urlRef.current);
    urlRef.current = URL.createObjectURL(file);
    setSrc(urlRef.current);
    setOuter(START_OUTER);
    setInner(START_INNER);
    setError("");
  }, []);

  const onDrop = useCallback(
    (e) => {
      e.preventDefault();
      take(e.dataTransfer?.files?.[0]);
    },
    [take]
  );

  const result = measure(outer, inner, side);
  const table = PSA_TOLERANCE[side];

  return (
    <div className="cen">
      <div className="cen-head">
        <h2 className="section-title">Grading</h2>
        <p className="cen-sub">
          How evenly a card was cut, and the grade that allows. Nothing is uploaded —
          the photo stays on your device.
        </p>
      </div>

      {/* The tab says Grading, which promises more than this measures. PSA
          weighs four things and this is one of them, so the other three are
          named rather than left as a gap for someone to fall into. */}
      <p className="cen-disclaimer">
        <strong>Centering is one of four things PSA grades on.</strong> Corners, edges
        and surface are judged separately, and any of them can pull a card below what
        its centering allows — never above it. Treat everything here as a ceiling, not
        a prediction, and not a substitute for their published standards.
      </p>

      {!src ? (
        <div className="cen-intro">
          <BorderDiagram />
          <div
            className="cen-drop"
            onDragOver={(e) => e.preventDefault()}
            onDrop={onDrop}
            onClick={() => fileRef.current?.click()}
          >
            <p className="cen-drop-main">Take a photo, or drop one here</p>
            <p className="cen-drop-hint">
              Straight on and filling the frame is worth more than a good camera — an
              angled shot measures the angle as much as the card.
            </p>
            <button type="button" className="btn btn-primary">Choose a photo</button>
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              className="cen-file"
              onChange={(e) => take(e.target.files?.[0])}
            />
          </div>
        </div>
      ) : (
        <div className="cen-work">
          <div className="cen-stage-wrap">
            <div className="cen-stage">
              <img className="cen-photo" src={src} alt="The card being measured" />
              {/* Numbered, because they are done in an order, and named for
                  where they go rather than for what they are called. */}
              <CardBox box={outer} onChange={setOuter} onProbe={setProbe}
                label="1 · Outside of card" variant="outer" />
              <CardBox box={inner} onChange={setInner} onProbe={setProbe}
                label="2 · Inside of border" variant="inner" />
              <Loupe src={src} at={probe} />
            </div>
          </div>

          <div className="cen-panel">
            {/* Kept within reach rather than only on the way in: the boxes are
                fiddly enough that people come back to check which is which. */}
            <details className="cen-help">
              <summary>Which box is which?</summary>
              <BorderDiagram compact />
            </details>

            <div className="cen-side">
              {["front", "back"].map((s) => (
                <button
                  key={s}
                  type="button"
                  className={`cen-side-btn${side === s ? " is-on" : ""}`}
                  onClick={() => setSide(s)}
                >
                  {s === "front" ? "Front" : "Back"}
                </button>
              ))}
            </div>

            {result ? (
              <>
                <div className="cen-readout">
                  <div className="cen-axis">
                    <span className="cen-axis-label">LEFT / RIGHT</span>
                    <span className="cen-axis-val">{asRatio(result.lr)}</span>
                  </div>
                  <div className="cen-axis">
                    <span className="cen-axis-label">TOP / BOTTOM</span>
                    <span className="cen-axis-val">{asRatio(result.tb)}</span>
                  </div>
                </div>

                {/* A ceiling, never a promise: centering cannot see a soft
                    corner, a scratch, a print line or a bad surface. */}
                <div className={`cen-verdict${result.ceiling?.grade === 10 ? " is-gem" : ""}`}>
                  {result.ceiling ? (
                    <>
                      <span className="cen-verdict-grade">
                        PSA {result.ceiling.grade} at best
                      </span>
                      <span className="cen-verdict-note">
                        {result.ceiling.label}, on centering alone. Corners, edges and
                        surface are graded separately and can only bring it down.
                      </span>
                    </>
                  ) : (
                    <>
                      <span className="cen-verdict-grade">Off the scale</span>
                      <span className="cen-verdict-note">
                        Further out than {table[table.length - 1].max}/
                        {100 - table[table.length - 1].max}, which is past the last
                        centering tolerance listed.
                      </span>
                    </>
                  )}
                </div>

                <ul className="cen-table">
                  {table.map((t) => {
                    const met = result.worst <= t.max;
                    return (
                      <li key={t.grade} className={met ? "is-met" : ""}>
                        <span>PSA {t.grade}</span>
                        <span>
                          {t.max}/{100 - t.max}
                        </span>
                        <span>{met ? "✓" : "—"}</span>
                      </li>
                    );
                  })}
                </ul>
                <p className="cen-fineprint">
                  {side === "front" ? "Front" : "Back"} tolerances, as PSA publishes
                  them. Worth checking against their current standards before you send
                  anything off.
                </p>

                {/* The precision this needs is finer than a finger has: on a
                    photo shown 320px wide the whole border is about 16px, and
                    a slip of one and a half of them is a 10 turned into a 9.
                    Saying so, and pointing at the two things that fix it, beats
                    letting a shaky drag read as a badly cut card. */}
                <p className="cen-fineprint cen-fineprint--warn">
                  A pixel of slop moves this about 3 points. Drag to get close — the
                  magnifier follows you — then <strong>arrow keys</strong> to nudge,
                  or <strong>alt + arrows</strong> for the fine step.
                </p>

              </>
            ) : (
              <p className="msg-empty">
                The border box has to sit inside the card box. Drag it back inside to
                get a reading.
              </p>
            )}

            <div className="cen-actions">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => { setOuter(START_OUTER); setInner(START_INNER); }}
              >
                Reset the boxes
              </button>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => { setSrc(null); setError(""); }}
              >
                Another card
              </button>
            </div>
          </div>
        </div>
      )}

      {error && <p className="msg-error">{error}</p>}
    </div>
  );
}
